import * as THREE from 'three';
import { getGrid, type GridGeometry } from '../../grid';
import type { GameState, MapDefinition, TileData, TilePosition, VisualAssetPlacement, Npc, NpcRuntimeData } from '../../model/gamestate/GameState';
import type { ActiveZoneData } from '../../spacetime/modules/SkillModule';
import { PortalTransitionAnimator } from '../skillVisualEffects/PortalTransitionAnimator';
import { CastTargetEffect } from '../skillVisualEffects/CastTargetEffect';
import { resolveRemainingTicks, withParsedPayload } from '../mapEvents/animationHandlers';
import { playSharedMapAnimation } from '../mapEvents/effectPlayback';
import { EntityManager } from '../tilemap/EntityManager';
import { COLORS, DEFAULT_FRAME_DELTA_MS, MAX_FRAME_DELTA_MS } from '../tilemap/constants';
import { Viewport } from '../tilemap/viewport';
import type {
  ActorHoverChangedDetail,
  ActorTargetDetail,
  EntityContextActionDetail,
  HoveredActorInfo,
  InterpolatedActorData,
  InterpolatedEntityState,
  PixelPosition,
  TileMapRendererOptions,
  TileSelectedDetail,
  VisualEffect,
} from '../tilemap/types';
import { createActorMesh, type ActorMeshBundle } from './assets/actors';
import { createNpcMesh } from './assets/npcs';
import { createVisualAssetMesh } from './assets/visualAssets';
import { createTileShape, createTileTopGeometry, getTileTopHeight, tileKey, tileWorldCenter, type TileWorldData } from './grid3d';
import { ThreeMeteorImpactEffect, ThreeMeteorTargetEffect, ThreeSmiteStrikeEffect, type ThreeEffectSceneHost } from './effects';
import { TerrainMaterialFactory } from './terrain/TerrainMaterialFactory';
import { TerrainTextureRegistry } from './terrain/TerrainTextureRegistry';
import { createProjectionController, type ProjectionController } from './camera';

type AnimationHandler = (payloadJson: string) => void;

interface ViewportHost {
  getWorldTileCenter(x: number, y: number, grid: GridGeometry): PixelPosition;
  projectTileCenterToScreen(x: number, y: number, grid: GridGeometry): PixelPosition;
  projectWorldCenterToScreen(center: PixelPosition): PixelPosition;
  pickTileAtScreen(screenX: number, screenY: number): TilePosition | null;
}

interface TileLayerAdapter {
  getMap(): MapDefinition;
  getGrid(): GridGeometry;
  setMap(map: MapDefinition): void;
}

interface ActorSceneRecord {
  signature: string;
  bundle: ActorMeshBundle;
}

interface StaticSceneRecord {
  object: THREE.Group;
  materials: THREE.MeshStandardMaterial[];
  labelOffset: number;
}

interface TilePickResult {
  tile: TilePosition;
  point: THREE.Vector3;
}

class ThreeViewport extends Viewport {
  public constructor(tileSize: number, private readonly host: ViewportHost) {
    super(tileSize);
  }

  public override worldToScreen(x: number, y: number, grid: GridGeometry): PixelPosition {
    return this.host.projectTileCenterToScreen(x, y, grid);
  }

  public override worldToScreenCenter(x: number, y: number, grid: GridGeometry): PixelPosition {
    return this.host.projectTileCenterToScreen(x, y, grid);
  }

  public override worldCenterToScreen(center: PixelPosition): PixelPosition {
    return this.host.projectWorldCenterToScreen(center);
  }

  public override worldTileCenter(x: number, y: number, grid: GridGeometry): PixelPosition {
    return this.host.getWorldTileCenter(x, y, grid);
  }

  public override screenToTile(
    canvasX: number,
    canvasY: number,
    _grid: GridGeometry,
    mapWidth: number,
    mapHeight: number,
  ): TilePosition | null {
    const tile = this.host.pickTileAtScreen(canvasX, canvasY);
    if (!tile) {
      return null;
    }
    if (tile.x < 0 || tile.y < 0 || tile.x >= mapWidth || tile.y >= mapHeight) {
      return null;
    }
    return tile;
  }
}

const DEFAULT_TILE_SIZE = 56;
const BOARD_PROJECTION_Y = 0.02;
const TILE_PICK_SURFACE_EPSILON = 0.002;
const LABEL_SCREEN_OFFSET_PX = 14;

function withAlpha(materials: THREE.MeshStandardMaterial[], opacity: number): void {
  for (const material of materials) {
    material.opacity = opacity;
  }
}

function disposeObject3D(object: THREE.Object3D): void {
  object.traverse((node: THREE.Object3D) => {
    const mesh = node as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    const material = (mesh as { material?: THREE.Material | THREE.Material[] }).material;
    if (Array.isArray(material)) {
      material.forEach((entry) => entry.dispose());
    } else {
      material?.dispose();
    }
  });
}

function isWholeTileCoordinate(value: number): boolean {
  return Number.isFinite(value) && Math.abs(value - Math.round(value)) < 1e-6;
}

export class TileMapRenderer extends EventTarget {
  public static async create(options: TileMapRendererOptions): Promise<TileMapRenderer> {
    const renderer = new TileMapRenderer(options);
    await renderer.initialize();
    return renderer;
  }

  private readonly container: HTMLElement;
  private readonly tileSize: number;
  private readonly overlayCanvas: HTMLCanvasElement;
  private overlayCtx: CanvasRenderingContext2D | null = null;
  private gameState: GameState;
  private destroyed = false;

  private readonly webglRenderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndcPointer = new THREE.Vector2();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -BOARD_PROJECTION_Y);
  private readonly worldPointScratch = new THREE.Vector3();
  private readonly actorLabelWorldScratch = new THREE.Vector3();
  private readonly projectionController: ProjectionController;

  private readonly terrainGroup = new THREE.Group();
  private readonly tileInteractionGroup = new THREE.Group();
  private readonly actorGroup = new THREE.Group();
  private readonly staticGroup = new THREE.Group();
  private readonly zoneGroup = new THREE.Group();
  private readonly highlightGroup = new THREE.Group();
  private readonly gridOverlayGroup = new THREE.Group();
  private readonly effectGroup = new THREE.Group();
  private readonly entityManager: EntityManager;
  private readonly viewport: Viewport;
  private readonly mapLayer: TileLayerAdapter;

  private readonly tilePickMeshes: THREE.Mesh[] = [];
  private readonly actorPickMeshes: THREE.Mesh[] = [];
  private readonly tileWorldByKey = new Map<string, TileWorldData>();
  private readonly tileMeshByKey = new Map<string, THREE.Mesh>();
  private readonly terrainTextures = new TerrainTextureRegistry();
  private readonly terrainMaterialFactory = new TerrainMaterialFactory(this.terrainTextures);
  private readonly actorRecords = new Map<string, ActorSceneRecord>();
  private readonly npcRecords = new Map<string, StaticSceneRecord>();
  private readonly assetRecords = new Map<string, StaticSceneRecord>();
  private readonly zoneRecords = new Map<string, THREE.Object3D>();

  private resizeObserver: ResizeObserver | null = null;
  private portalTransition: PortalTransitionAnimator | null = null;
  private lastCharacterPosition: TilePosition | null = null;
  private selfSnapTarget: TilePosition | null = null;
  private cameraFocus: TilePosition | null = null;
  private cameraFocusWorldCenter: PixelPosition | null = null;
  private localCharacterRotation = 180;
  private playerDisplayName = '';
  private animationFrameId: number | null = null;
  private lastFrameTimestamp: number | null = null;
  private pendingRender = false;
  private isRenderingFrame = false;
  private activeZones: ActiveZoneData[] = [];
  private queuePreview: (TilePosition | null)[] = [];
  private activeRangeCenter: TilePosition | null = null;
  private activeRangeGroupDirty = true;
  private staticWorldDirty = true;
  private zoneMeshesDirty = true;
  private skillTargetingActive = false;
  private hoveredTile: TilePosition | null = null;
  private hoveredTileInFog = false;
  private hoveredActor: HoveredActorInfo | null = null;
  private hoverPointer: PixelPosition | null = null;
  private hoverActorRing: THREE.Mesh | null = null;
  private entityContextMenu: HTMLDivElement | null = null;
  private entityContextIdButton: HTMLButtonElement | null = null;
  private entityContextInventoryButton: HTMLButtonElement | null = null;
  private entityContextTradeButton: HTMLButtonElement | null = null;
  private contextMenuActor: HoveredActorInfo | null = null;
  private readonly unsubscribeTerrainTextureUpdates: () => void;

  private constructor(options: TileMapRendererOptions) {
    super();
    this.container = options.container;
    this.tileSize = options.tileSize ?? DEFAULT_TILE_SIZE;
    this.gameState = options.gameState;
    const initialMap = options.map;
    const initialGrid = getGrid(initialMap.gridType);

    this.overlayCanvas = document.createElement('canvas');
    this.overlayCanvas.style.position = 'absolute';
    this.overlayCanvas.style.inset = '0';
    this.overlayCanvas.style.touchAction = 'none';
    this.overlayCanvas.style.userSelect = 'none';

    this.webglRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.webglRenderer.domElement.style.position = 'absolute';
    this.webglRenderer.domElement.style.inset = '0';
    this.webglRenderer.domElement.style.pointerEvents = 'none';
    this.webglRenderer.setPixelRatio(window.devicePixelRatio || 1);
    this.projectionController = createProjectionController(options.projectionMode ?? 'perspective');

    this.entityManager = new EntityManager();

    let currentMap = initialMap;
    let currentGrid = initialGrid;
    this.mapLayer = {
      getMap: () => currentMap,
      getGrid: () => currentGrid,
      setMap: (nextMap: MapDefinition) => {
        currentMap = nextMap;
        currentGrid = getGrid(nextMap.gridType);
      },
    };

    this.viewport = new ThreeViewport(this.tileSize, {
      getWorldTileCenter: (x: number, y: number, grid: GridGeometry): PixelPosition => {
        if (isWholeTileCoordinate(x) && isWholeTileCoordinate(y)) {
          const key = tileKey(Math.round(x), Math.round(y));
          const cached = this.tileWorldByKey.get(key);
          if (cached) {
            return { x: cached.center.x, y: cached.center.z };
          }
        }
        const center = tileWorldCenter(grid, this.tileSize, x, y);
        return { x: center.x, y: center.z };
      },
      projectTileCenterToScreen: (x: number, y: number, grid: GridGeometry): PixelPosition => {
        const center = this.viewport.worldTileCenter(x, y, grid);
        return this.projectWorldPoint(center.x, center.y, BOARD_PROJECTION_Y);
      },
      projectWorldCenterToScreen: (center: PixelPosition): PixelPosition => this.projectWorldPoint(center.x, center.y, BOARD_PROJECTION_Y),
      pickTileAtScreen: (screenX: number, screenY: number): TilePosition | null => this.pickTileAtScreen(screenX, screenY),
    });

    if (getComputedStyle(this.container).position === 'static') {
      this.container.style.position = 'relative';
    }
    this.container.style.overflow = 'hidden';
    this.container.style.background = 'radial-gradient(circle at top, #1f2937 0%, #0b1120 72%)';

    this.scene.background = new THREE.Color('#09111d');
    this.scene.fog = new THREE.Fog('#09111d', 12, 38);
    this.scene.add(
      this.terrainGroup,
      this.tileInteractionGroup,
      this.staticGroup,
      this.actorGroup,
      this.zoneGroup,
      this.highlightGroup,
      this.gridOverlayGroup,
      this.effectGroup,
    );

    const ambient = new THREE.HemisphereLight('#dbeafe', '#020617', 1.25);
    ambient.position.set(0, 8, 0);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight('#fff7ed', 1.3);
    sun.position.set(-6, 10, 4);
    this.scene.add(sun);

    this.unsubscribeTerrainTextureUpdates = this.terrainTextures.subscribe(() => {
      this.rebuildMap();
      this.scheduleRender();
    });
  }

  public get view(): HTMLElement | null {
    return this.destroyed ? null : this.overlayCanvas;
  }

  public get activeRadius(): number {
    return this.gameState.getViewRadius();
  }

  public getCameraFocus(): TilePosition | null {
    return this.cameraFocus;
  }

  public getCurrentMap(): MapDefinition {
    return this.mapLayer.getMap();
  }

  public getCurrentGrid(): GridGeometry {
    return this.mapLayer.getGrid();
  }

  public getViewSize(): { width: number; height: number } {
    return {
      width: this.overlayCanvas.width,
      height: this.overlayCanvas.height,
    };
  }

  public setCameraFocus(position: TilePosition): void {
    this.cameraFocus = { ...position };
    const center = this.viewport.worldTileCenter(position.x, position.y, this.getCurrentGrid());
    this.cameraFocusWorldCenter = { x: center.x, y: center.y };
    this.scheduleRender();
  }

  public rotateCameraOrbit(direction: 'left' | 'right'): void {
    this.projectionController.rotateOrbit(direction);
    this.scheduleRender();
  }

  public panCameraByScreen(deltaX: number, deltaY: number): void {
    const baseCenter = this.cameraFocusWorldCenter
      ?? (this.cameraFocus
        ? this.viewport.worldTileCenter(this.cameraFocus.x, this.cameraFocus.y, this.getCurrentGrid())
        : null)
      ?? (this.lastCharacterPosition
        ? this.viewport.worldTileCenter(this.lastCharacterPosition.x, this.lastCharacterPosition.y, this.getCurrentGrid())
        : null);
    if (!baseCenter) {
      return;
    }

    const viewWidth = this.overlayCanvas.width;
    const viewHeight = this.overlayCanvas.height;
    if (viewWidth <= 0 || viewHeight <= 0) {
      return;
    }

    const centerScreen = { x: viewWidth / 2, y: viewHeight / 2 };
    const boardY = BOARD_PROJECTION_Y;
    const centerWorld = this.projectScreenPointToGround(centerScreen.x, centerScreen.y, boardY);
    const rightWorld = this.projectScreenPointToGround(centerScreen.x + 1, centerScreen.y, boardY);
    const downWorld = this.projectScreenPointToGround(centerScreen.x, centerScreen.y + 1, boardY);
    if (!centerWorld || !rightWorld || !downWorld) {
      return;
    }

    const panDeltaX = (rightWorld.x - centerWorld.x) * deltaX + (downWorld.x - centerWorld.x) * deltaY;
    const panDeltaZ = (rightWorld.z - centerWorld.z) * deltaX + (downWorld.z - centerWorld.z) * deltaY;
    this.cameraFocusWorldCenter = {
      x: baseCenter.x + panDeltaX,
      y: baseCenter.y + panDeltaZ,
    };
    this.projectionController.snapToTarget(new THREE.Vector3(
      this.cameraFocusWorldCenter.x,
      boardY,
      this.cameraFocusWorldCenter.y,
    ));
    this.scheduleRender();
  }

  public getVisualCharacterPosition(): TilePosition | null {
    const selfState = this.entityManager.getSelfActorState(this.gameState);
    return selfState?.current ?? this.lastCharacterPosition;
  }

  public worldToScreenCenter(x: number, y: number): PixelPosition {
    return this.viewport.worldToScreenCenter(x, y, this.getCurrentGrid());
  }

  public worldCenterToScreen(center: PixelPosition): PixelPosition {
    return this.viewport.worldCenterToScreen(center);
  }

  public mapPositionToScreen(mapX: number, mapY: number): PixelPosition | null {
    if (!Number.isFinite(mapX) || !Number.isFinite(mapY)) {
      return null;
    }
    return this.projectWorldPoint(mapX, mapY, BOARD_PROJECTION_Y);
  }

  public screenToTile(screenX: number, screenY: number): TilePosition | null {
    const map = this.getCurrentMap();
    return this.viewport.screenToTile(screenX, screenY, this.getCurrentGrid(), map.width, map.height);
  }

  public screenToMapPosition(event: MouseEvent | DragEvent): PixelPosition | null {
    const pointer = this.getPointerCoordinates(event);
    if (!pointer) {
      return null;
    }
    const groundPoint = this.raycastTileSurface(pointer.x, pointer.y) ?? this.raycastGround(pointer.x, pointer.y);
    if (!groundPoint) {
      return null;
    }
    return {
      x: groundPoint.x / this.tileSize,
      y: groundPoint.z / this.tileSize,
    };
  }

  public markActorSnap(entityId: string, target: TilePosition): void {
    this.entityManager.markActorSnap(entityId, target);
  }

  public setCharacterPosition(position: TilePosition, options?: { immediate?: boolean; rotation?: number }): void {
    const previousTarget = this.lastCharacterPosition;
    this.lastCharacterPosition = { ...position };
    this.localCharacterRotation =
      options?.rotation !== undefined
        ? options.rotation
        : this.gameState.getCharacterRotation();

    if (options?.immediate || !previousTarget) {
      this.selfSnapTarget = { ...position };
      if (!this.cameraFocus) {
        this.cameraFocus = { ...position };
        const center = this.viewport.worldTileCenter(position.x, position.y, this.getCurrentGrid());
        this.cameraFocusWorldCenter = { x: center.x, y: center.y };
      }
    }

    this.activeRangeCenter = { ...position };
    this.activeRangeGroupDirty = true;
    this.scheduleRender();
  }

  public setQueuePreview(queue: (TilePosition | null)[]): void {
    this.queuePreview = queue.slice();
    this.scheduleRender();
  }

  public setActiveZones(zones: ActiveZoneData[]): void {
    this.activeZones = zones;
    this.zoneMeshesDirty = true;
    this.scheduleRender();
  }

  public requestRender(): void {
    this.scheduleRender();
  }

  public setPlayerDisplayName(displayName: string): void {
    this.playerDisplayName = displayName;
    const selfState = this.entityManager.getSelfActorState(this.gameState);
    if (selfState) {
      selfState.data = { ...selfState.data, displayName: displayName || undefined };
    }
    this.scheduleRender();
  }

  public setSkillTargetingActive(active: boolean): void {
    this.skillTargetingActive = active;
  }

  public addEffect(effect: VisualEffect): void {
    if (this.isPortalTransitioning()) {
      effect.disposeScene?.();
      return;
    }
    this.entityManager.addEffect(effect);
    this.scheduleRender();
  }

  public isPortalTransitioning(): boolean {
    return Boolean(this.portalTransition?.isActive());
  }

  public startPortalTransition(onFadeOutComplete: () => void | Promise<void>, onComplete: () => void): void {
    if (this.portalTransition?.isActive()) {
      return;
    }
    this.entityManager.clearEffects();
    this.portalTransition = new PortalTransitionAnimator();
    this.portalTransition.startTransition({
      onFadeOutComplete,
      onComplete: () => {
        this.portalTransition = null;
        onComplete();
      },
    });
    this.scheduleRender();
  }

  public cancelPortalTransition(): void {
    if (!this.portalTransition) {
      return;
    }
    this.portalTransition.cancelAnimation();
    this.portalTransition = null;
    this.scheduleRender();
  }

  public playAnimation(animationName: string, payloadJson: string): void {
    if (playSharedMapAnimation({
      animationName,
      payloadJson,
      tileSize: this.tileSize,
      tileToScreenCenter: this.createTileToScreenCenter(),
      resolveEntityPosition: (entityId) => this.resolveRenderableEntityPosition(entityId),
      addEffect: (effect) => this.addEffect(effect),
    })) {
      return;
    }

    const handler = this.animationHandlers[animationName];
    if (handler) {
      handler(payloadJson);
    }
  }

  public startPortalFadeIn(): void {
    if (!this.portalTransition) {
      return;
    }
    this.portalTransition.startFadeIn();
    this.scheduleRender();
  }

  public swapToNewMap(map: MapDefinition, _mapId: string): void {
    this.mapLayer.setMap(map);
    this.entityManager.clear();
    this.activeZones = [];
    this.queuePreview = [];
    this.hoveredTile = null;
    this.hoveredActor = null;
    this.clearHoverActorRing();
    this.rebuildMap();
    this.scheduleRender();
  }

  public refreshCurrentMap(map: MapDefinition): void {
    this.mapLayer.setMap(map);
    this.rebuildMap();
    this.scheduleRender();
  }

  public destroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.overlayCanvas.removeEventListener('pointermove', this.handlePointerMove);
    this.overlayCanvas.removeEventListener('pointerleave', this.handlePointerLeave);
    this.overlayCanvas.removeEventListener('click', this.handleClick);
    this.overlayCanvas.removeEventListener('contextmenu', this.handleContextMenu);
    this.overlayCanvas.removeEventListener('wheel', this.handleWheel);
    this.gameState.removeEventListener('stateChanged', this.handleStateChanged as EventListener);
    window.removeEventListener('pointerdown', this.handleGlobalPointerDown, true);
    window.removeEventListener('blur', this.handleWindowBlur);
    this.stopRenderLoop();
    this.hideEntityContextMenu();
    this.entityContextMenu?.remove();
    this.entityContextMenu = null;
    this.clearSceneContent();
    this.unsubscribeTerrainTextureUpdates();
    this.terrainTextures.dispose();
    this.webglRenderer.dispose();
    this.container.innerHTML = '';
  }

  private async initialize(): Promise<void> {
    this.container.innerHTML = '';
    this.container.appendChild(this.webglRenderer.domElement);
    this.container.appendChild(this.overlayCanvas);

    this.overlayCtx = this.overlayCanvas.getContext('2d');
    if (!this.overlayCtx) {
      throw new Error('Failed to initialize 3D overlay canvas');
    }
    this.overlayCtx.imageSmoothingEnabled = true;

    this.overlayCanvas.addEventListener('pointermove', this.handlePointerMove);
    this.overlayCanvas.addEventListener('pointerleave', this.handlePointerLeave);
    this.overlayCanvas.addEventListener('click', this.handleClick);
    this.overlayCanvas.addEventListener('contextmenu', this.handleContextMenu);
    this.overlayCanvas.addEventListener('wheel', this.handleWheel, { passive: false });
    this.gameState.addEventListener('stateChanged', this.handleStateChanged as EventListener);
    window.addEventListener('pointerdown', this.handleGlobalPointerDown, true);
    window.addEventListener('blur', this.handleWindowBlur);

    this.rebuildMap();
    this.syncCanvasDimensions();
    this.setupResizeObserver();
    this.scheduleRender();
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.syncCanvasDimensions();
      this.scheduleRender();
    });
    this.resizeObserver.observe(this.container);
  }

  private syncCanvasDimensions(): void {
    const rect = this.container.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    this.webglRenderer.setSize(width, height, false);
    this.webglRenderer.domElement.style.width = `${width}px`;
    this.webglRenderer.domElement.style.height = `${height}px`;
    this.overlayCanvas.width = width;
    this.overlayCanvas.height = height;
    this.overlayCanvas.style.width = `${width}px`;
    this.overlayCanvas.style.height = `${height}px`;
    this.projectionController.syncViewport(width, height);
  }

  private rebuildMap(): void {
    this.clearSceneContent();
    this.tileWorldByKey.clear();
    this.tileMeshByKey.clear();
    this.tilePickMeshes.length = 0;
    this.staticWorldDirty = true;
    this.activeRangeGroupDirty = true;
    this.zoneMeshesDirty = true;

    const map = this.getCurrentMap();
    const grid = this.getCurrentGrid();
    for (let y = 0; y < map.height; y += 1) {
      for (let x = 0; x < map.width; x += 1) {
        const tile = map.tiles[y][x];
        if (!tile) {
          continue;
        }
        const geometry = createTileTopGeometry(map.gridType, tile);
        const material = this.terrainMaterialFactory.createTileTopCapMaterial(tile);
        const mesh = new THREE.Mesh(geometry, material);
        const center = tileWorldCenter(grid, this.tileSize, x, y);
        mesh.position.set(center.x, getTileTopHeight(tile), center.z);
        mesh.userData = {
          tile,
          baseColor: material.color.clone(),
          baseOpacity: material.opacity,
          isPortal: tile.terrain === 'portal',
        };
        this.terrainGroup.add(mesh);
        this.tileMeshByKey.set(tileKey(x, y), mesh);
        this.tileWorldByKey.set(tileKey(x, y), {
          center,
          topY: getTileTopHeight(tile),
        });

        const pickGeometry = new THREE.ShapeGeometry(createTileShape(map.gridType, tile));
        pickGeometry.rotateX(-Math.PI / 2);
        const pickMesh = new THREE.Mesh(
          pickGeometry,
          new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            depthWrite: false,
            side: THREE.DoubleSide,
          }),
        );
        pickMesh.position.set(center.x, getTileTopHeight(tile) + TILE_PICK_SURFACE_EPSILON, center.z);
        pickMesh.userData = { tile };
        this.tileInteractionGroup.add(pickMesh);
        this.tilePickMeshes.push(pickMesh);

        const edge = new THREE.LineSegments(
          new THREE.EdgesGeometry(geometry),
          new THREE.LineBasicMaterial({ color: 0x0f172a, transparent: true, opacity: 0.3 }),
        );
        edge.position.copy(mesh.position);
        this.gridOverlayGroup.add(edge);
      }
    }
  }

  private clearSceneContent(): void {
    for (const group of [this.terrainGroup, this.tileInteractionGroup, this.actorGroup, this.staticGroup, this.zoneGroup, this.highlightGroup, this.gridOverlayGroup, this.effectGroup]) {
      while (group.children.length > 0) {
        const child = group.children[0];
        if (!child) {
          continue;
        }
        group.remove(child);
        disposeObject3D(child);
      }
      group.clear();
    }
    this.actorRecords.clear();
    this.actorPickMeshes.length = 0;
    this.npcRecords.clear();
    this.assetRecords.clear();
    this.zoneRecords.clear();
  }

  private stopRenderLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.pendingRender = false;
  }

  private scheduleRender(): void {
    if (this.destroyed) {
      return;
    }
    this.pendingRender = true;
    if (this.isRenderingFrame) {
      return;
    }
    if (this.animationFrameId === null) {
      this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
    }
  }

  private readonly handleAnimationFrame = (time: number): void => {
    this.animationFrameId = null;
    this.pendingRender = false;
    this.isRenderingFrame = true;
    const shouldContinue = this.renderScene(time);
    this.isRenderingFrame = false;
    if (shouldContinue || this.pendingRender) {
      this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
    } else {
      this.lastFrameTimestamp = null;
    }
  };

  private renderScene(timestamp?: number): boolean {
    if (!this.overlayCtx) {
      return false;
    }

    const deltaMsRaw = timestamp !== undefined && this.lastFrameTimestamp !== null
      ? timestamp - this.lastFrameTimestamp
      : DEFAULT_FRAME_DELTA_MS;
    const deltaMs = Math.min(Math.max(deltaMsRaw, 0), MAX_FRAME_DELTA_MS);
    const deltaSeconds = deltaMs / 1000;
    this.lastFrameTimestamp = timestamp ?? this.lastFrameTimestamp ?? performance.now();

    const { hasActiveAnimation, clearedSnapTarget } = this.entityManager.updateActorVisuals(
      deltaSeconds,
      this.gameState,
      this.lastCharacterPosition,
      this.selfSnapTarget,
      this.localCharacterRotation,
      this.playerDisplayName,
      (position) => this.viewport.worldTileCenter(position.x, position.y, this.getCurrentGrid()),
    );
    if (clearedSnapTarget) {
      this.selfSnapTarget = null;
    }

    this.syncStaticWorldObjects();
    this.syncZoneMeshes();
    this.syncActorMeshes();
    this.syncTileVisualState();
    this.syncActiveRangeMeshes();
    this.syncHoverMeshes();
    const cameraAnimating = this.updateCamera();

    this.webglRenderer.render(this.scene, this.projectionController.camera);
    this.drawOverlay(deltaSeconds);

    const hasPortalTransition = Boolean(this.portalTransition?.isActive());
    const effectsActive = this.entityManager.getActiveEffects().length > 0;
    return hasActiveAnimation || hasPortalTransition || effectsActive || cameraAnimating;
  }

  private syncTileVisualState(): void {
    const focus = this.lastCharacterPosition;
    const grid = this.getCurrentGrid();
    const fullMap = this.getCurrentMap().visibilityMode === 'full_map';
    for (const mesh of this.tileMeshByKey.values()) {
      const material = mesh.material as THREE.MeshBasicMaterial;
      const tile = mesh.userData.tile as TileData;
      const baseColor = mesh.userData.baseColor as THREE.Color;
      const baseOpacity = mesh.userData.baseOpacity as number;
      const visible = fullMap || !focus || grid.distance(Math.floor(focus.x), Math.floor(focus.y), tile.x, tile.y) <= this.activeRadius;
      material.color.copy(baseColor).multiplyScalar(visible ? 1 : 0.35);
      material.opacity = tile.biome === 'water' ? (visible ? baseOpacity : Math.min(baseOpacity, 0.45)) : baseOpacity;
      if (this.hoveredTile && this.hoveredTile.x === tile.x && this.hoveredTile.y === tile.y) {
        material.color.lerp(
          new THREE.Color(this.hoveredTileInFog ? '#cbd5e1' : '#f8fafc'),
          this.hoveredTileInFog ? 0.12 : 0.18,
        );
      }
    }
  }

  private syncStaticWorldObjects(): void {
    if (!this.staticWorldDirty) {
      return;
    }
    this.staticWorldDirty = false;
    this.syncVisualAssets(this.gameState.getVisualAssets());
    this.syncNpcs(this.gameState.getNpcs());
    this.syncRuntimeChests(this.gameState.getNpcRuntime().filter((npc) => npc.npcType === 'chest'));
  }

  private syncVisualAssets(assets: VisualAssetPlacement[]): void {
    const liveKeys = new Set<string>();
    const grid = this.getCurrentGrid();
    const map = this.getCurrentMap();
    for (const asset of assets) {
      const key = `asset:${asset.id}`;
      liveKeys.add(key);
      const tilePosition = grid.pixelToTile(asset.mapX * this.tileSize, asset.mapY * this.tileSize, this.tileSize);
      const tileX = Math.max(0, Math.min(map.width - 1, tilePosition.q));
      const tileY = Math.max(0, Math.min(map.height - 1, tilePosition.r));
      const tile = this.tileWorldByKey.get(tileKey(tileX, tileY));
      if (!tile) {
        continue;
      }
      let record = this.assetRecords.get(key);
      if (!record) {
        const bundle = createVisualAssetMesh(asset.assetId);
        record = {
          object: bundle.group,
          materials: bundle.materials,
          labelOffset: bundle.labelOffset,
        };
        this.assetRecords.set(key, record);
        this.staticGroup.add(record.object);
      }
      record.object.position.set(asset.mapX, tile.topY, asset.mapY);
    }
    for (const [key, record] of this.assetRecords.entries()) {
      if (liveKeys.has(key)) {
        continue;
      }
      this.assetRecords.delete(key);
      this.staticGroup.remove(record.object);
      disposeObject3D(record.object);
    }
  }

  private syncNpcs(npcs: Npc[]): void {
    const liveKeys = new Set<string>();
    for (const npc of npcs) {
      const key = `npc:${npc.npcId}`;
      liveKeys.add(key);
      const tile = this.tileWorldByKey.get(tileKey(npc.x, npc.y));
      if (!tile) {
        continue;
      }
      let record = this.npcRecords.get(key);
      if (!record) {
        const bundle = createNpcMesh(npc.npcType, npc.width, npc.height);
        record = { object: bundle.group, materials: bundle.materials, labelOffset: bundle.labelOffset };
        this.npcRecords.set(key, record);
        this.staticGroup.add(record.object);
      }
      record.object.position.set(tile.center.x, tile.topY, tile.center.z);
    }
    for (const [key, record] of this.npcRecords.entries()) {
      if (liveKeys.has(key) || key.startsWith('chest-runtime:')) {
        continue;
      }
      this.npcRecords.delete(key);
      this.staticGroup.remove(record.object);
      disposeObject3D(record.object);
    }
  }

  private syncRuntimeChests(npcs: NpcRuntimeData[]): void {
    const liveKeys = new Set<string>();
    for (const npc of npcs) {
      const key = `chest-runtime:${npc.npcId}`;
      liveKeys.add(key);
      const tile = this.tileWorldByKey.get(tileKey(npc.x, npc.y));
      if (!tile) {
        continue;
      }
      let record = this.npcRecords.get(key);
      if (!record) {
        const bundle = createNpcMesh('chest');
        record = { object: bundle.group, materials: bundle.materials, labelOffset: bundle.labelOffset };
        this.npcRecords.set(key, record);
        this.staticGroup.add(record.object);
      }
      record.object.position.set(tile.center.x, tile.topY, tile.center.z);
    }
    for (const [key, record] of this.npcRecords.entries()) {
      if (!key.startsWith('chest-runtime:') || liveKeys.has(key)) {
        continue;
      }
      this.npcRecords.delete(key);
      this.staticGroup.remove(record.object);
      disposeObject3D(record.object);
    }
  }

  private syncZoneMeshes(): void {
    if (!this.zoneMeshesDirty) {
      return;
    }
    this.zoneMeshesDirty = false;
    const liveKeys = new Set<string>();
    for (const zone of this.activeZones) {
      if (zone.zoneType !== 'poison_fog' && zone.zoneType !== 'poison_cloud') {
        continue;
      }
      const key = `${zone.zoneType}:${zone.centerX}:${zone.centerY}:${zone.radius}`;
      liveKeys.add(key);
      const center = this.viewport.worldTileCenter(zone.centerX, zone.centerY, this.getCurrentGrid());
      let mesh = this.zoneRecords.get(key);
      if (!mesh) {
        const color = zone.zoneType === 'poison_cloud' ? '#a3e635' : '#65a30d';
        const material = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: zone.zoneType === 'poison_cloud' ? 0.18 : 0.12,
          depthWrite: false,
        });
        mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(zone.radius + 0.75, zone.radius + 0.75, 0.04, 32),
          material,
        );
        this.zoneRecords.set(key, mesh);
        this.zoneGroup.add(mesh);
      }
      mesh.position.set(center.x, 0.04, center.y);
    }
    for (const [key, mesh] of this.zoneRecords.entries()) {
      if (liveKeys.has(key)) {
        continue;
      }
      this.zoneRecords.delete(key);
      this.zoneGroup.remove(mesh);
      disposeObject3D(mesh);
    }
  }

  private syncActorMeshes(): void {
    const liveActorIds = new Set<string>();
    this.actorPickMeshes.length = 0;

    for (const [actorId, state] of this.entityManager.getActorVisuals().entries()) {
      liveActorIds.add(actorId);
      const data = state.data;
      const signature = `${data.kind}|${data.entityType ?? ''}|${data.color ?? ''}|${data.accessoriesJson ?? ''}`;
      let record = this.actorRecords.get(actorId);
      if (!record || record.signature !== signature) {
        if (record) {
          this.actorGroup.remove(record.bundle.group);
          disposeObject3D(record.bundle.group);
        }
        const bundle = createActorMesh({
          kind: data.kind,
          entityType: data.entityType,
          color: data.color || (data.kind === 'mob' ? '#ef4444' : '#3b82f6'),
          accessoriesJson: data.accessoriesJson,
        });
        bundle.pickMesh.userData = { actorId };
        record = { signature, bundle };
        this.actorRecords.set(actorId, record);
        this.actorGroup.add(bundle.group);
      }

      this.actorPickMeshes.push(record.bundle.pickMesh);
      this.applyActorState(record, state);
    }

    for (const [actorId, record] of this.actorRecords.entries()) {
      if (liveActorIds.has(actorId)) {
        continue;
      }
      this.actorRecords.delete(actorId);
      this.actorGroup.remove(record.bundle.group);
      disposeObject3D(record.bundle.group);
    }
  }

  private applyActorState(record: ActorSceneRecord, state: InterpolatedEntityState<InterpolatedActorData>): void {
    const ground = this.getTileTopY(Math.round(state.current.x), Math.round(state.current.y));
    const center = state.currentCenter;
    record.bundle.group.position.set(center.x, ground, center.y);
    record.bundle.group.rotation.y = THREE.MathUtils.degToRad(180 - (state.data.rotation ?? 180));
    const dimmed = this.isTileInFog(state.current);
    const alpha = state.opacity * (dimmed ? 0.35 : 1);
    withAlpha(record.bundle.materials, alpha);
  }

  private syncActiveRangeMeshes(): void {
    if (!this.activeRangeGroupDirty) {
      return;
    }
    this.activeRangeGroupDirty = false;
    while (this.highlightGroup.children.length > 0) {
      const child = this.highlightGroup.children[0];
      if (!child) {
        continue;
      }
      this.highlightGroup.remove(child);
      disposeObject3D(child);
    }

    if (!this.activeRangeCenter) {
      return;
    }

    const map = this.getCurrentMap();
    const grid = this.getCurrentGrid();
    const radius = this.activeRadius;
    const cx = Math.floor(this.activeRangeCenter.x);
    const cy = Math.floor(this.activeRangeCenter.y);

    for (let y = Math.max(0, cy - radius); y <= Math.min(map.height - 1, cy + radius); y += 1) {
      for (let x = Math.max(0, cx - radius); x <= Math.min(map.width - 1, cx + radius); x += 1) {
        if (grid.distance(cx, cy, x, y) > radius) {
          continue;
        }
        const tile = map.tiles[y][x];
        const shape = createTileShape(map.gridType, tile);
        const geometry = new THREE.ShapeGeometry(shape);
        geometry.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshBasicMaterial({
            color: '#ffffff',
            transparent: true,
            opacity: 0.11,
            depthWrite: false,
          }),
        );
        const tileWorld = this.tileWorldByKey.get(tileKey(x, y));
        if (!tileWorld) {
          continue;
        }
        mesh.position.set(tileWorld.center.x, tileWorld.topY + 0.015, tileWorld.center.z);
        this.highlightGroup.add(mesh);
      }
    }
  }

  private syncHoverMeshes(): void {
    this.clearHoverActorRing();
    if (!this.hoveredActor) {
      return;
    }
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: '#f8fafc',
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
    });
    this.hoverActorRing = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.03, 8, 18), ringMaterial);
    const record = this.actorRecords.get(this.hoveredActor.id);
    if (record) {
      this.hoverActorRing.position.copy(record.bundle.group.position);
      this.hoverActorRing.position.y += 0.06;
      this.hoverActorRing.rotation.x = Math.PI / 2;
      this.highlightGroup.add(this.hoverActorRing);
    }
  }

  private clearHoverActorRing(): void {
    if (!this.hoverActorRing) {
      return;
    }
    this.highlightGroup.remove(this.hoverActorRing);
    disposeObject3D(this.hoverActorRing);
    this.hoverActorRing = null;
  }

  private projectScreenPointToGround(screenX: number, screenY: number, groundY: number): THREE.Vector3 | null {
    if (this.overlayCanvas.width <= 0 || this.overlayCanvas.height <= 0) {
      return null;
    }

    this.projectionController.setRayFromScreen(
      screenX,
      screenY,
      this.overlayCanvas.width,
      this.overlayCanvas.height,
      this.raycaster,
      this.ndcPointer,
    );

    const origin = this.raycaster.ray.origin;
    const direction = this.raycaster.ray.direction;
    if (Math.abs(direction.y) < 1e-5) {
      return null;
    }

    const distance = (groundY - origin.y) / direction.y;
    if (!Number.isFinite(distance)) {
      return null;
    }

    return origin.clone().addScaledVector(direction, distance);
  }

  private updateCamera(): boolean {
    const focusWorld = this.cameraFocusWorldCenter
      ?? (this.cameraFocus
        ? this.viewport.worldTileCenter(this.cameraFocus.x, this.cameraFocus.y, this.getCurrentGrid())
        : null)
      ?? (() => {
        const fallback = this.entityManager.getSelfActorState(this.gameState)?.current ?? this.lastCharacterPosition;
        if (!fallback) {
          return null;
        }
        return this.viewport.worldTileCenter(fallback.x, fallback.y, this.getCurrentGrid());
      })();
    if (!focusWorld) {
      return false;
    }
    return this.projectionController.updateCamera(new THREE.Vector3(focusWorld.x, BOARD_PROJECTION_Y, focusWorld.y));
  }

  private drawOverlay(deltaSeconds: number): void {
    if (!this.overlayCtx) {
      return;
    }
    const ctx = this.overlayCtx;
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    this.drawQueueOverlay(ctx);
    this.drawActorLabels(ctx);
    this.drawHoveredActorTooltip(ctx);
    const hasPortalTransition = Boolean(this.portalTransition?.isActive());
    if (hasPortalTransition && this.portalTransition) {
      this.portalTransition.drawOverlay(ctx, this.overlayCanvas.width, this.overlayCanvas.height);
    }
    this.entityManager.updateEffects(deltaSeconds);
    this.syncSceneEffects(deltaSeconds);
    this.entityManager.renderEffectsOverlay(ctx);
  }

  private syncSceneEffects(deltaSeconds: number): void {
    const host: ThreeEffectSceneHost = { effectGroup: this.effectGroup };
    for (const effect of this.entityManager.getActiveEffects()) {
      if (effect.attachToScene && !(effect as { __threeAttached?: boolean }).__threeAttached) {
        effect.attachToScene(host);
        (effect as { __threeAttached?: boolean }).__threeAttached = true;
      }
      effect.updateScene?.(deltaSeconds);
    }
  }

  private drawQueueOverlay(ctx: CanvasRenderingContext2D): void {
    const selfState = this.entityManager.getSelfActorState(this.gameState);
    if (!selfState || this.queuePreview.length === 0) {
      return;
    }

    const points: PixelPosition[] = [this.worldToScreenCenter(selfState.current.x, selfState.current.y)];
    for (const tile of this.queuePreview) {
      if (!tile) {
        continue;
      }
      points.push(this.worldToScreenCenter(tile.x, tile.y));
    }
    if (points.length < 2) {
      return;
    }

    ctx.save();
    ctx.strokeStyle = COLORS.pathLine;
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 10]);
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let index = 1; index < points.length; index += 1) {
      ctx.lineTo(points[index].x, points[index].y);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let index = 1; index < points.length; index += 1) {
      const point = points[index];
      ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
      ctx.beginPath();
      ctx.arc(point.x, point.y, 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#f8fafc';
      ctx.fillText(String(index), point.x, point.y + 0.5);
    }
    ctx.restore();
  }

  private drawActorLabels(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.font = '12px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    for (const [actorId, state] of this.entityManager.getActorVisuals().entries()) {
      const record = this.actorRecords.get(actorId);
      if (!record) {
        continue;
      }
      const opacity = record.bundle.materials[0]?.opacity ?? 1;
      if (opacity <= 0.1) {
        continue;
      }
      const anchorWorld = record.bundle.pickMesh.getWorldPosition(this.actorLabelWorldScratch);
      const anchor = this.projectWorldPoint(anchorWorld.x, anchorWorld.z, anchorWorld.y);
      const screen = {
        x: anchor.x,
        y: anchor.y - LABEL_SCREEN_OFFSET_PX,
      };
      const label = state.data.displayName?.trim() || (state.data.kind === 'mob' ? formatMobName(state.data.entityType) : '');
      if (label) {
        ctx.fillStyle = `rgba(248, 250, 252, ${Math.min(1, opacity)})`;
        ctx.fillText(label, screen.x, screen.y);
      }

      if (typeof state.data.hp === 'number' && typeof state.data.maxHp === 'number' && state.data.maxHp > 0) {
        const barWidth = 34;
        const hpRatio = Math.max(0, Math.min(1, state.data.hp / state.data.maxHp));
        const barX = screen.x - barWidth / 2;
        const barY = screen.y - 16;
        ctx.fillStyle = `rgba(15, 23, 42, ${0.85 * opacity})`;
        ctx.fillRect(barX, barY, barWidth, 4);
        ctx.fillStyle = `rgba(34, 197, 94, ${0.9 * opacity})`;
        ctx.fillRect(barX, barY, barWidth * hpRatio, 4);
        if (typeof state.data.stamina === 'number' && typeof state.data.maxStamina === 'number' && state.data.maxStamina > 0) {
          const staminaRatio = Math.max(0, Math.min(1, state.data.stamina / state.data.maxStamina));
          ctx.fillStyle = `rgba(15, 23, 42, ${0.85 * opacity})`;
          ctx.fillRect(barX, barY + 6, barWidth, 4);
          ctx.fillStyle = `rgba(34, 197, 94, ${0.9 * opacity})`;
          ctx.fillRect(barX, barY + 6, barWidth * staminaRatio, 4);
        }
      }
    }

    ctx.restore();
  }

  private drawHoveredActorTooltip(ctx: CanvasRenderingContext2D): void {
    if (!this.hoveredActor || !this.hoverPointer || typeof this.hoveredActor.hp !== 'number') {
      return;
    }
    ctx.save();
    ctx.font = '12px Arial, sans-serif';
    const hpText = Math.round(this.hoveredActor.hp);
    const maxHpText = typeof this.hoveredActor.maxHp === 'number' ? ` / ${Math.round(this.hoveredActor.maxHp)}` : '';
    const staminaText =
      typeof this.hoveredActor.stamina === 'number'
        ? `  ST: ${Math.round(this.hoveredActor.stamina)}${typeof this.hoveredActor.maxStamina === 'number' ? ` / ${Math.round(this.hoveredActor.maxStamina)}` : ''}`
        : '';
    const text = `HP: ${hpText}${maxHpText}${staminaText}`;
    const paddingX = 8;
    const width = ctx.measureText(text).width + paddingX * 2;
    const height = 22;
    const drawX = Math.max(8, Math.min(this.hoverPointer.x + 12, this.overlayCanvas.width - width - 8));
    const drawY = Math.max(8, Math.min(this.hoverPointer.y - 28, this.overlayCanvas.height - height - 8));
    ctx.fillStyle = 'rgba(10, 14, 20, 0.92)';
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.7)';
    ctx.lineWidth = 1;
    ctx.fillRect(drawX, drawY, width, height);
    ctx.strokeRect(drawX, drawY, width, height);
    ctx.fillStyle = '#e5e7eb';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, drawX + paddingX, drawY + height / 2 + 0.5);
    ctx.restore();
  }

  private projectWorldPoint(worldX: number, worldZ: number, worldY = 0): PixelPosition {
    return this.projectionController.projectWorldPoint(
      worldX,
      worldY,
      worldZ,
      this.overlayCanvas.width,
      this.overlayCanvas.height,
      this.worldPointScratch,
    );
  }

  private getTileTopY(x: number, y: number): number {
    return this.tileWorldByKey.get(tileKey(x, y))?.topY ?? 0.08;
  }

  private isTileInFog(tile: TilePosition): boolean {
    if (!this.lastCharacterPosition) {
      return false;
    }
    if (this.getCurrentMap().visibilityMode === 'full_map') {
      return false;
    }
    return this.getCurrentGrid().distance(
      Math.floor(this.lastCharacterPosition.x),
      Math.floor(this.lastCharacterPosition.y),
      Math.round(tile.x),
      Math.round(tile.y),
    ) > this.activeRadius;
  }

  private readonly handleStateChanged = (event: Event): void => {
    const detail = (event as CustomEvent<{ type: string }>).detail;
    if (
      detail.type === 'visualAssetsUpdated'
      || detail.type === 'npcsUpdated'
      || detail.type === 'npcRuntimeUpdated'
      || detail.type === 'mapTransitionComplete'
      || detail.type === 'portalTransitionComplete'
      || detail.type === 'portalTransitionCancelled'
    ) {
      this.staticWorldDirty = true;
    }
    this.scheduleRender();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    const pointer = this.getPointerCoordinates(event);
    this.hoverPointer = pointer ? { ...pointer } : null;
    if (!pointer) {
      this.clearHoverState();
      return;
    }
    const tilePick = this.resolveTilePickAtScreen(pointer.x, pointer.y);

    const actor = this.pickActorAtScreen(pointer.x, pointer.y);
    this.setHoveredActor(actor);
    if (!tilePick) {
      this.setHoveredTile(null, false);
      this.overlayCanvas.style.cursor = actor ? 'pointer' : '';
      return;
    }
    const inFog = this.isTileInFog(tilePick.tile);
    this.setHoveredTile(tilePick.tile, inFog);
    this.overlayCanvas.style.cursor = 'pointer';

    if ((event.buttons & 0b11) !== 0) {
      this.dispatchEvent(new CustomEvent<TileSelectedDetail>('tileSelected', {
        detail: {
          tile: this.getCurrentMap().tiles[tilePick.tile.y][tilePick.tile.x],
          tilePosition: { x: tilePick.tile.x, y: tilePick.tile.y },
          mapPosition: {
            x: tilePick.point.x / this.tileSize,
            y: tilePick.point.z / this.tileSize,
          },
          screenPosition: { x: pointer.x, y: pointer.y },
          shiftKey: event.shiftKey,
          button: (event.buttons & 0b10) !== 0 ? 2 : 0,
          buttons: event.buttons,
        },
      }));
    }
  };

  private readonly handlePointerLeave = (): void => {
    this.hoverPointer = null;
    this.clearHoverState();
    this.overlayCanvas.style.cursor = '';
  };

  private readonly handleClick = (event: MouseEvent): void => {
    this.hideEntityContextMenu();
    if (event.button !== 0) {
      return;
    }
    const pointer = this.getPointerCoordinates(event);
    if (!pointer) {
      return;
    }
    const actor = this.pickActorAtScreen(pointer.x, pointer.y);
    const actorDetail = actor ? this.toActorTargetDetail(actor) : null;
    if (actorDetail) {
      const actorSelectedEvent = new CustomEvent<ActorTargetDetail>('actorSelected', {
        detail: actorDetail,
        cancelable: true,
      });
      this.dispatchEvent(actorSelectedEvent);
      if (actorSelectedEvent.defaultPrevented) {
        return;
      }
    }
    const tilePick = this.resolveTilePickAtScreen(pointer.x, pointer.y);
    if (!tilePick) {
      return;
    }
    this.dispatchEvent(new CustomEvent<TileSelectedDetail>('tileSelected', {
      detail: {
        tile: this.getCurrentMap().tiles[tilePick.tile.y][tilePick.tile.x],
        tilePosition: { x: tilePick.tile.x, y: tilePick.tile.y },
        mapPosition: {
          x: tilePick.point.x / this.tileSize,
          y: tilePick.point.z / this.tileSize,
        },
        screenPosition: { x: pointer.x, y: pointer.y },
        shiftKey: event.shiftKey,
        button: event.button,
        buttons: event.buttons,
      },
    }));
  };

  private readonly handleContextMenu = (event: MouseEvent): void => {
    const pointer = this.getPointerCoordinates(event);
    if (!pointer) {
      return;
    }
    if (this.skillTargetingActive) {
      event.preventDefault();
      this.hideEntityContextMenu();
      this.dispatchEvent(new Event('skillTargetingCancelled'));
      return;
    }

    const actor = this.pickActorAtScreen(pointer.x, pointer.y);
    if (actor && actor.kind !== 'self') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showEntityContextMenu(pointer, actor);
      return;
    }

    const tilePick = this.resolveTilePickAtScreen(pointer.x, pointer.y);
    if (!tilePick) {
      return;
    }
    event.preventDefault();
    this.hideEntityContextMenu();
    this.dispatchEvent(new CustomEvent<TileSelectedDetail>('tileSelected', {
      detail: {
        tile: this.getCurrentMap().tiles[tilePick.tile.y][tilePick.tile.x],
        mapPosition: {
          x: tilePick.point.x / this.tileSize,
          y: tilePick.point.z / this.tileSize,
        },
        screenPosition: { x: pointer.x, y: pointer.y },
        shiftKey: event.shiftKey,
        button: event.button,
        buttons: event.buttons,
      },
    }));
  };

  private readonly handleWheel = (event: WheelEvent): void => {
    event.preventDefault();
    if (event.ctrlKey) {
      if (!this.projectionController.adjustZoom(event.deltaY)) {
        return;
      }
      this.scheduleRender();
      return;
    }
    if (!this.projectionController.adjustTilt(event.deltaY)) {
      return;
    }
    this.scheduleRender();
  };

  private readonly handleGlobalPointerDown = (event: PointerEvent): void => {
    if (!this.entityContextMenu || this.entityContextMenu.style.display === 'none') {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.entityContextMenu.contains(target)) {
      return;
    }
    this.hideEntityContextMenu();
  };

  private readonly handleWindowBlur = (): void => {
    this.hideEntityContextMenu();
  };

  private clearHoverState(): void {
    this.setHoveredActor(null);
    this.setHoveredTile(null, false);
    this.scheduleRender();
  }

  private setHoveredTile(tile: TilePosition | null, inFog: boolean): void {
    const previous = this.hoveredTile;
    if (
      previous?.x === tile?.x
      && previous?.y === tile?.y
      && this.hoveredTileInFog === inFog
    ) {
      return;
    }
    this.hoveredTile = tile ? { ...tile } : null;
    this.hoveredTileInFog = inFog;
    this.dispatchEvent(new CustomEvent('tileHoverChanged', {
      detail: {
        tile: tile ? { ...tile } : null,
        inFog,
      },
    }));
    this.scheduleRender();
  }

  private setHoveredActor(actor: HoveredActorInfo | null): void {
    const previousId = this.hoveredActor?.id ?? null;
    const nextId = actor?.id ?? null;
    if (previousId === nextId && previousId !== null) {
      this.hoveredActor = actor;
      return;
    }
    this.hoveredActor = actor;
    this.dispatchEvent(new CustomEvent<ActorHoverChangedDetail>('actorHoverChanged', {
      detail: {
        actor: actor ? this.toActorTargetDetail(actor) : null,
      },
    }));
    this.scheduleRender();
  }

  private toActorTargetDetail(actor: HoveredActorInfo): ActorTargetDetail {
    return {
      actorId: actor.id,
      kind: actor.kind,
      displayName: actor.displayName?.trim() || actor.id,
      position: { x: actor.position.x, y: actor.position.y },
    };
  }

  private pickTileAtScreen(screenX: number, screenY: number): TilePosition | null {
    return this.resolveTilePickAtScreen(screenX, screenY)?.tile ?? null;
  }

  private resolveTilePickAtScreen(screenX: number, screenY: number): TilePickResult | null {
    if (this.isPortalTransitioning()) {
      return null;
    }
    this.setRayFromScreen(screenX, screenY);
    const hits = this.raycaster.intersectObjects(Array.from(this.tileMeshByKey.values()), false);
    for (const hit of hits) {
      const mesh = hit.object as THREE.Mesh;
      const tile = mesh.userData.tile as TileData | undefined;
      const face = hit.face;
      if (!tile || !face) {
        continue;
      }
      const normalY = face.normal.clone().transformDirection(mesh.matrixWorld).y;
      if (normalY < 0.85) {
        continue;
      }
      return {
        tile: { x: tile.x, y: tile.y },
        point: hit.point.clone(),
      };
    }
    return null;
  }

  private pickActorAtScreen(screenX: number, screenY: number): HoveredActorInfo | null {
    this.setRayFromScreen(screenX, screenY);
    const hits = this.raycaster.intersectObjects(this.actorPickMeshes, false);
    const actorId = hits[0]?.object.userData.actorId as string | undefined;
    if (!actorId) {
      return null;
    }
    const state = this.entityManager.getActorVisuals().get(actorId);
    const record = this.actorRecords.get(actorId);
    if (!state || !record) {
      return null;
    }
    return {
      id: state.data.id,
      playerId: state.data.playerId,
      kind: state.data.kind,
      entityType: state.data.entityType,
      color: state.data.color,
      accessoriesJson: state.data.accessoriesJson,
      center: this.projectWorldPoint(record.bundle.group.position.x, record.bundle.group.position.z, record.bundle.group.position.y + 0.45),
      position: { x: state.data.position.x, y: state.data.position.y },
      displayName: state.data.displayName,
      tradeTargetName: state.data.kind === 'remotePlayer' ? (state.data.displayName?.trim() || state.data.id) : undefined,
      hp: state.data.hp,
      maxHp: state.data.maxHp,
      rotation: state.data.rotation ?? 180,
      mobInventoryValue: state.data.mobInventoryValue ?? null,
      mobInventoryItemsJson: state.data.mobInventoryItemsJson ?? null,
    };
  }

  private setRayFromScreen(screenX: number, screenY: number): void {
    this.projectionController.setRayFromScreen(
      screenX,
      screenY,
      this.overlayCanvas.width,
      this.overlayCanvas.height,
      this.raycaster,
      this.ndcPointer,
    );
  }

  private raycastGround(screenX: number, screenY: number): THREE.Vector3 | null {
    this.setRayFromScreen(screenX, screenY);
    return this.raycaster.ray.intersectPlane(this.groundPlane, new THREE.Vector3()) ?? null;
  }

  private raycastTileSurface(screenX: number, screenY: number): THREE.Vector3 | null {
    this.setRayFromScreen(screenX, screenY);
    const hit = this.raycaster.intersectObjects(this.tilePickMeshes, false)[0];
    return hit?.point.clone() ?? null;
  }

  private getPointerCoordinates(event: MouseEvent | PointerEvent): PixelPosition | null {
    const rect = this.overlayCanvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return null;
    }
    const scaleX = this.overlayCanvas.width / rect.width;
    const scaleY = this.overlayCanvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  private ensureEntityContextMenu(): void {
    if (this.entityContextMenu) {
      return;
    }
    const menu = document.createElement('div');
    Object.assign(menu.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      display: 'none',
      background: 'rgba(15, 23, 42, 0.96)',
      border: '1px solid #334155',
      borderRadius: '6px',
      padding: '4px',
      zIndex: '1200',
      minWidth: '120px',
      boxShadow: '0 8px 22px rgba(0, 0, 0, 0.45)',
      pointerEvents: 'auto',
    });
    menu.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
    });

    const makeButton = (label: string): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      Object.assign(button.style, {
        width: '100%',
        textAlign: 'left',
        padding: '7px 10px',
        background: 'transparent',
        border: 'none',
        color: '#e2e8f0',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.82rem',
      });
      button.addEventListener('mouseenter', () => {
        button.style.background = 'rgba(96, 165, 250, 0.2)';
      });
      button.addEventListener('mouseleave', () => {
        button.style.background = 'transparent';
      });
      return button;
    };

    const idButton = makeButton('ID');
    const inventoryButton = makeButton('Inventory');
    const tradeButton = makeButton('Trade');
    Object.assign(tradeButton.style, {
      borderTop: '1px solid rgba(148, 163, 184, 0.25)',
      marginTop: '4px',
      paddingTop: '9px',
    });

    idButton.addEventListener('click', () => this.openContextProfile('id'));
    inventoryButton.addEventListener('click', () => this.openContextProfile('inventory'));
    tradeButton.addEventListener('click', () => {
      if (!this.contextMenuActor) {
        return;
      }
      const targetName = this.contextMenuActor.tradeTargetName?.trim();
      if (!targetName) {
        this.hideEntityContextMenu();
        return;
      }
      this.dispatchEvent(new CustomEvent<EntityContextActionDetail>('entityContextAction', {
        detail: { action: 'trade', targetName },
      }));
      this.hideEntityContextMenu();
    });

    menu.appendChild(idButton);
    menu.appendChild(inventoryButton);
    menu.appendChild(tradeButton);
    this.container.appendChild(menu);

    this.entityContextMenu = menu;
    this.entityContextIdButton = idButton;
    this.entityContextInventoryButton = inventoryButton;
    this.entityContextTradeButton = tradeButton;
  }

  private openContextProfile(action: 'id' | 'inventory'): void {
    if (!this.contextMenuActor || this.contextMenuActor.kind === 'self') {
      this.hideEntityContextMenu();
      return;
    }
    const actor = this.contextMenuActor;
    this.dispatchEvent(new CustomEvent<EntityContextActionDetail>('entityContextAction', {
      detail: {
        action,
        profile: {
          actorId: actor.id,
          playerId: actor.playerId,
          kind: actor.kind,
          entityType: actor.entityType,
          name: actor.displayName?.trim() || formatMobName(actor.entityType),
          hp: actor.hp,
          maxHp: actor.maxHp,
          stamina: actor.stamina,
          maxStamina: actor.maxStamina,
          rotation: actor.rotation,
          mobInventoryValue: actor.mobInventoryValue ?? null,
          mobInventoryItemsJson: actor.mobInventoryItemsJson ?? null,
        },
      },
    }));
    this.hideEntityContextMenu();
  }

  private showEntityContextMenu(pointer: PixelPosition, actor: HoveredActorInfo): void {
    this.ensureEntityContextMenu();
    if (
      !this.entityContextMenu
      || !this.entityContextTradeButton
      || !this.entityContextIdButton
      || !this.entityContextInventoryButton
    ) {
      return;
    }

    this.contextMenuActor = actor;
    this.entityContextTradeButton.disabled = actor.kind === 'mob';
    this.entityContextTradeButton.style.opacity = actor.kind === 'mob' ? '0.5' : '1';
    this.entityContextTradeButton.style.cursor = actor.kind === 'mob' ? 'not-allowed' : 'pointer';
    this.entityContextIdButton.disabled = false;
    this.entityContextInventoryButton.disabled = false;

    this.entityContextMenu.style.display = 'block';
    const menuRect = this.entityContextMenu.getBoundingClientRect();
    const maxLeft = Math.max(0, this.overlayCanvas.width - menuRect.width - 6);
    const maxTop = Math.max(0, this.overlayCanvas.height - menuRect.height - 6);
    const left = Math.max(6, Math.min(pointer.x + 10, maxLeft));
    const top = Math.max(6, Math.min(pointer.y + 8, maxTop));
    this.entityContextMenu.style.left = `${left}px`;
    this.entityContextMenu.style.top = `${top}px`;
  }

  private hideEntityContextMenu(): void {
    this.contextMenuActor = null;
    if (!this.entityContextMenu) {
      return;
    }
    this.entityContextMenu.style.display = 'none';
  }

  private readonly animationHandlers: Record<string, AnimationHandler> = {
    skill_cast_start: (payloadJson) => this.handleSkillCastStart(payloadJson),
    summon_meteor: (payloadJson) => this.handleSummonMeteor(payloadJson),
  };

  private createTileToScreenCenter() {
    return (x: number, y: number) => this.worldToScreenCenter(x, y);
  }

  private resolveRenderableEntityPosition(entityId: string): { x: number; y: number } | null {
    const visuals = this.entityManager.getActorVisuals();
    const entityNode =
      visuals.get(entityId)
      || (entityId === this.gameState.getSelfEntityId()
        ? this.entityManager.getSelfActorState(this.gameState)
        : null);
    return entityNode ? { x: entityNode.current.x, y: entityNode.current.y } : null;
  }

  private handleSkillCastStart(payloadJson: string): void {
    withParsedPayload<{ skillId?: string; targetX?: number; targetY?: number; resolveTick?: number }>('skill_cast_start', payloadJson, (payload) => {
      const { skillId, targetX, targetY, resolveTick } = payload;
      if (typeof targetX !== 'number' || typeof targetY !== 'number') {
        return;
      }
      const remainingTicks = resolveRemainingTicks(this.gameState.getTick(), { skillId, targetX, targetY, resolveTick });
      const isMeteor = skillId === 'summon_meteor';
      if (isMeteor) {
        this.addEffect(new ThreeMeteorTargetEffect({
          x: targetX,
          y: targetY,
          radiusTiles: 2,
          durationSeconds: remainingTicks,
          color: '#ff914d',
          map: this.getCurrentMap(),
          grid: this.getCurrentGrid(),
          tileSize: this.tileSize,
        }));
        return;
      }
      this.addEffect(new CastTargetEffect({
        x: targetX,
        y: targetY,
        radiusTiles: 0.75,
        durationSeconds: remainingTicks,
        tileToScreenCenter: this.createTileToScreenCenter(),
        tileSize: this.tileSize,
        strokeColor: 'rgba(250, 204, 21, 0.9)',
        fillColor: 'rgba(180, 83, 9, 0.3)',
      }));
    });
  }

  private handleSummonMeteor(payloadJson: string): void {
    withParsedPayload<{ x?: number; y?: number; radius?: number }>('summon_meteor', payloadJson, (payload) => {
      const { x, y, radius } = payload;
      if (typeof x !== 'number' || typeof y !== 'number') {
        return;
      }
      const safeRadius = typeof radius === 'number' && Number.isFinite(radius) && radius >= 0 ? radius : 2;
      this.addEffect(new ThreeSmiteStrikeEffect({
        x,
        y,
        map: this.getCurrentMap(),
        grid: this.getCurrentGrid(),
        tileSize: this.tileSize,
      }));
      this.addEffect(new ThreeMeteorImpactEffect({
        x,
        y,
        radius: safeRadius,
        map: this.getCurrentMap(),
        grid: this.getCurrentGrid(),
        tileSize: this.tileSize,
      }));
    });
  }
}

function formatMobName(entityType?: string): string {
  if (!entityType || !entityType.trim()) {
    return 'Mob';
  }
  return entityType
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

function roundDebug(value: number): number {
  return Math.round(value * 100) / 100;
}
